"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Portfolio() {
  const modules = [{
    number: "01.",
    name: "ORBITAL SENSING",
    description: "We engineer high-fidelity sensing arrays designed for the harsh vacuum of space. Every sensor is calibrated for extreme thermal variance and precise data acquisition — never approximation, always mission-critical.",
    deliverables: ["Multi-spectral array design", "Thermal-vacuum stress testing", "Signal-to-noise optimization", "Radiation-hardened architecture"],
    cta: "REQUEST TECHNICAL BRIEF",
    stat: "SUB-METER RESOLUTION AT ORBIT",
    image: {
      src: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/06/Copilot_20260629_120038.webp?v=2wwf19q6cp4",
      srcset: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/06/Copilot_20260629_120038.webp?v=ln03ewac0nn-wvc-srcset",
      sizes: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/06/Copilot_20260629_120038.webp?v=k8phkw6nv0b-wvc-sizes",
      alt: "High-fidelity satellite sensor array in deep space"
    }
  }, {
    number: "02.",
    name: "DATA LINK",
    description: "Secure, low-latency communication protocols that bridge the gap between orbital assets and ground command. Every packet is encrypted and verified for integrity across the entire signal chain.",
    deliverables: ["Encrypted telemetry streams", "Low-latency protocol design", "Ground-to-space synchronization", "Redundant link architecture"],
    cta: "REQUEST TECHNICAL BRIEF",
    stat: "99.99% LINK RELIABILITY",
    image: {
      src: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-01_03_24-PM.png?v=lj8i9zwyc2",
      srcset: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-01_03_24-PM.png?v=sl2i0025clk-wvc-srcset",
      sizes: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-01_03_24-PM.png?v=uocaagur45-wvc-sizes",
      alt: "Digital signal visualization representing secure data transmission"
    }
  }, {
    number: "03.",
    name: "INTELLIGENCE",
    description: "Command-grade analytics that transform raw telemetry into actionable intelligence. We process orbital data through proprietary AI algorithms to provide a clear, high-contrast view of the theater.",
    deliverables: ["Automated threat detection", "Predictive orbital modeling", "Real-time situational awareness", "Command-ready data visualization"],
    cta: "REQUEST TECHNICAL BRIEF",
    stat: "MILLISECONDS PROCESSING LATENCY",
    image: {
      src: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-10_33_31-AM_TIALINX.png?v=yitxrvg8ima",
      srcset: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-10_33_31-AM_TIALINX.png?v=lx51o2r887-wvc-srcset",
      sizes: "https://bil.eci.mybluehost.me/wp-content/uploads/2026/07/ChatGPT-Image-Jul-1-2026-10_33_31-AM_TIALINX.png?v=zrx78i6qu6-wvc-sizes",
      alt: "Command-grade data visualization interface"
    }
  }];
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3508" id="advanced-sensing-portfolio" className="bg-[#080F1A] text-[#B2BDC7] py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {}
        <div ref={header.ref} className={`transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[12px] uppercase tracking-[0.2em] mb-10 md:mb-14" style={{
          color: "#6697B9"
        }}>OUR CAPABILITIES&nbsp;</p>
          <h2 className="font-default font-bold leading-[1.1] tracking-tighter text-[#B2BDC7] text-[44px] sm:text-[64px] md:text-[84px] lg:text-[96px]">
            <span className="block">ORBITAL</span>
            <span className="block">INTELLIGENCE</span>
            <span className="block" style={{
            WebkitTextStroke: "1.5px #6697B9",
            color: "transparent"
          }}>
              COMMAND
            </span>
            <span className="block">SYSTEMS.</span>
          </h2>
        </div>

        {}
        <div className="mt-24 md:mt-32 lg:mt-40 flex flex-col gap-24 md:gap-32 lg:gap-36">
          {modules.map((m, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          const isReversed = i % 2 === 1;
          return <div key={i} data-index={i} ref={reveal.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                {}
                <div className={`order-2 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className={`font-default font-bold leading-none tracking-tighter text-[64px] md:text-[88px] lg:text-[120px] transition-transform duration-1000 ease-out ${reveal.isVisible ? "-translate-y-2" : "translate-y-4"}`} style={{
                WebkitTextStroke: "1.5px #6697B9",
                color: "transparent"
              }}>
                    {m.number}
                  </div>

                  <h3 className="font-default font-semibold leading-[1.2] tracking-[0.05em] uppercase text-[#B2BDC7] text-[36px] md:text-[48px] lg:text-[56px] mt-4 md:mt-6">
                    {m.name}
                  </h3>

                  <p className="font-default font-normal text-[#B2BDC7]/90 text-lg md:text-xl leading-relaxed mt-6 max-w-[480px]">
                    {m.description}
                  </p>

                  <ul className="mt-8 flex flex-col gap-3 max-w-[480px]">
                    {m.deliverables.map((d, di) => <li key={di} data-index={di} className="flex items-start gap-3 font-default text-base text-[#45596F]">
                        <span className="inline-block mt-[10px] shrink-0" style={{
                    width: "5px",
                    height: "5px",
                    backgroundColor: "#6697B9"
                  }} aria-hidden="true" />
                        <span>{d}</span>
                      </li>)}
                  </ul>

                  <div className="mt-10">
                    <Link to="mailto:info@tialinx.com">
                      <Button variant="ghost" className="px-0 h-auto py-2 font-default font-bold uppercase tracking-[0.15em] text-sm hover:bg-transparent group" style={{
                    color: "#6697B9"
                  }}>
                        {m.cta}
                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {}
               <div className={`order-1 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
  <div className="w-full border border-[#45596F] bg-[#080F1A]">
    <img src={m.image.src} data-wvc-srcset={m.image.srcset} data-wvc-sizes={m.image.sizes} alt={m.image.alt} loading="lazy" decoding="async" className="w-full h-auto object-contain" />
  </div>

  <div className="mt-6 w-full max-w-[320px] backdrop-blur-sm px-5 py-4 border border-[#45596F]" style={{
                backgroundColor: "rgba(8, 15, 26, 0.8)"
              }}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1" style={{
                  color: "#6697B9"
                }}>
      TELEMETRY DATA
    </p>

    <p className="font-default font-bold text-[#B2BDC7] text-sm md:text-base leading-snug">
      {m.stat}
    </p>
  </div>
</div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}